<template>
  <div>
    <!-- Query List -->
    <div v-if="queries.length > 0" class="mb-4">
      <v-card
        v-for="(query, index) in queries"
        :key="index"
        class="mb-3"
        variant="outlined"
      >
        <v-card-text>
          <div class="d-flex align-start gap-2">
            <!-- Drag Handle -->
            <v-icon class="drag-handle mt-2" size="small">mdi-drag</v-icon>

            <!-- Query Input -->
            <div class="flex-grow-1">
              <v-textarea
                :ref="el => setTextareaRef(el, index)"
                v-model="query.query_text"
                label="Поисковый запрос"
                hint="Используйте операторы: и, или, не, &quot;&quot; и др."
                persistent-hint
                rows="3"
                variant="outlined"
                :error-messages="query.error"
                @update:model-value="validateQuery(index)"
                @blur="saveCursorPosition(index)"
              >
                <template v-slot:append-inner>
                  <v-menu location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-help-circle"
                        variant="text"
                        size="small"
                      ></v-btn>
                    </template>
                    <v-card max-width="400">
                      <v-card-title class="text-subtitle-2">
                        Операторы QLang
                        <HelpHint section="projects" anchor="qlang" text="Язык запросов с примерами — в справке" />
                      </v-card-title>
                      <v-card-text class="text-caption">
                        <div class="mb-2"><strong>или, or</strong> - логическое ИЛИ</div>
                        <div class="mb-2"><strong>и, and</strong> - логическое И</div>
                        <div class="mb-2"><strong>не, not</strong> - логическое НЕ (исключить)</div>
                        <div class="mb-2"><strong>"фраза"</strong> - точное словосочетание</div>
                        <div class="mb-2"><strong>(группировка)</strong> - приоритет операторов</div>
                        <div class="mb-2"><strong>=термин</strong> - точная форма (без склонений)</div>
                        <div class="mb-2"><strong>слово/цс</strong> - целое слово (не часть другого)</div>
                        <div class="mb-2"><strong>слово /сN слово</strong> - близость в пределах N слов</div>
                        <div class="mb-2"><strong>слово :N слово</strong> - следование в пределах N</div>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </template>
              </v-textarea>

              <!-- Operator Buttons -->
              <div class="mb-3">
                <v-btn-group variant="outlined" density="compact" divided>
                  <v-btn
                    v-for="op in operators"
                    :key="op.value"
                    @click="insertOperator(index, op.value)"
                    size="small"
                  >
                    <v-tooltip activator="parent" location="top">
                      {{ op.tooltip }}
                    </v-tooltip>
                    {{ op.label }}
                  </v-btn>
                </v-btn-group>
              </div>

              <!-- Query Settings -->
              <div class="d-flex gap-4 mb-2">
                <!--
                  TODO: "Область поиска" (search_in per query) — hidden until backend supports
                  per-query search_in during project execution. See master task "search scope support".
                  Backend field ProjectQuery.search_in is preserved.
                -->

                <!--
                  TODO: "Вес запроса" (weight) — hidden until backend uses weight
                  for ranking/scoring findings. See master task #44.
                  Backend field ProjectQuery.weight is preserved.
                -->

                <!--
                  TODO: "Исключающий запрос" (is_excluded) — hidden until backend implements
                  exclusion logic in project_search_listener.py and project_matching_engine.py.
                  See master task #43. Backend field ProjectQuery.is_excluded is preserved.
                -->
              </div>
            </div>

            <!-- Actions -->
            <div class="d-flex flex-column gap-2">
              <v-btn
                icon="mdi-arrow-up"
                variant="text"
                size="small"
                :disabled="index === 0"
                @click="moveUp(index)"
              ></v-btn>
              <v-btn
                icon="mdi-arrow-down"
                variant="text"
                size="small"
                :disabled="index === queries.length - 1"
                @click="moveDown(index)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                variant="text"
                size="small"
                color="error"
                @click="removeQuery(index)"
              ></v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Add Query Button -->
    <v-btn
      variant="outlined"
      color="primary"
      prepend-icon="mdi-plus"
      @click="addQuery"
      block
    >
      Добавить запрос
    </v-btn>

    <!-- Generate from natural language -->
    <v-btn
      variant="text"
      color="primary"
      prepend-icon="mdi-auto-fix"
      class="mt-2"
      block
      @click="openGenerator"
    >
      Сгенерировать из описания
    </v-btn>

    <!-- Generator dialog -->
    <v-dialog v-model="genDialog" max-width="640">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon start>mdi-auto-fix</v-icon>
          Генерация запроса из описания
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="genText"
            label="Опишите, что нужно найти"
            placeholder="Например: упоминания Аэрофлота, но не про авиакомпанию Победа"
            rows="3"
            auto-grow
            variant="outlined"
            :disabled="genLoading"
            @keydown.ctrl.enter="runGenerate"
          />
          <v-btn
            color="primary"
            prepend-icon="mdi-creation"
            :loading="genLoading"
            :disabled="!genText.trim()"
            @click="runGenerate"
          >
            Сгенерировать
          </v-btn>

          <v-alert
            v-if="genError"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ genError }}
          </v-alert>

          <div v-if="genResult" class="mt-4">
            <v-textarea
              v-model="genResult.query"
              label="QLang-запрос (можно отредактировать)"
              rows="2"
              auto-grow
              variant="outlined"
              :error-messages="genResult.valid ? '' : (genResult.error || 'Запрос не прошёл валидацию')"
              @blur="runCount"
            />
            <div v-if="genResult.explanation" class="text-caption text-medium-emphasis mb-2">
              {{ genResult.explanation }}
            </div>
            <div class="d-flex align-center ga-2">
              <v-chip
                :color="genResult.valid ? 'success' : 'error'"
                size="small"
                variant="tonal"
              >
                {{ genResult.valid ? 'Валиден' : 'Невалиден' }}
              </v-chip>
              <span class="text-caption text-medium-emphasis">
                <template v-if="genCountLoading">подсчёт…</template>
                <template v-else-if="genCount !== null">≈ {{ genCount }} совпадений в базе за 30 дней</template>
              </span>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="genDialog = false">Отмена</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!genResult || !genResult.query || !genResult.query.trim()"
            @click="insertGenerated"
          >
            Вставить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Help Text -->
    <v-alert
      type="info"
      variant="tonal"
      density="compact"
      class="mt-4"
    >
      <template v-slot:text>
        <div class="mb-2">
          <strong>Язык запросов QLang:</strong> Используйте операторы для построения сложных запросов.
        </div>
        <div class="text-caption">
          Примеры:
          <ul class="ml-4">
            <li>билайн или beeline</li>
            <li>"уральские авиалинии"</li>
            <li>(сбербанк или "сбер банк") и ипотека</li>
            <li>аэрофлот не победа — Аэрофлот, но без «Победы»</li>
            <li>сво/цс — найдёт "СВО", но не "присвоением"</li>
          </ul>
        </div>
      </template>
    </v-alert>

    <!-- Validation Summary -->
    <v-alert
      v-if="hasErrors"
      type="error"
      variant="tonal"
      density="compact"
      class="mt-2"
    >
      <template v-slot:text>
        Исправьте ошибки в запросах перед сохранением
      </template>
    </v-alert>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import apiClient from '@/apiClient';
import apiDataClient from '@/apiDataClient';

// Props and emits
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  searchIn: {
    type: String,
    default: 'everywhere',
  },
});

const emit = defineEmits(['update:modelValue']);

// Refs
const queries = ref([]);
const textareaRefs = ref({});
const cursorPositions = ref({});  // Store cursor positions per query index

// Set textarea ref for each query
const setTextareaRef = (el, index) => {
  if (el) {
    textareaRefs.value[index] = el;
  }
};

// Save cursor position when textarea loses focus
const saveCursorPosition = (index) => {
  const vTextarea = textareaRefs.value[index];
  if (vTextarea) {
    // Get the native textarea element from Vuetify component
    const textarea = vTextarea.$el?.querySelector('textarea');
    if (textarea) {
      cursorPositions.value[index] = {
        start: textarea.selectionStart,
        end: textarea.selectionEnd
      };
    }
  }
};

// Operators configuration (только реально поддерживаемые парсером)
const operators = [
  { label: 'или', value: ' или ', tooltip: 'Логическое ИЛИ' },
  { label: 'и', value: ' и ', tooltip: 'Логическое И' },
  { label: 'не', value: ' не ', tooltip: 'Логическое НЕ (исключить)' },
  { label: '""', value: '""', tooltip: 'Точная фраза' },
  { label: '=', value: '=', tooltip: 'Точная форма (без склонений)' },
  { label: '/цс', value: '/цс', tooltip: 'Целое слово (не часть другого)' },
  { label: '/сN', value: '/с', tooltip: 'Близость в словах' },
  { label: '()', value: '()', tooltip: 'Группировка' },
];

// Search in options
const searchInOptions = [
  { text: 'По умолчанию', value: null },
  { text: 'Везде', value: 'everywhere' },
  { text: 'Только в заголовках', value: 'titles_only' },
];

const weightOptions = [
  { title: '1 (минимальный)', value: 1 },
  { title: '2', value: 2 },
  { title: '3', value: 3 },
  { title: '4', value: 4 },
  { title: '5 (средний)', value: 5 },
  { title: '6', value: 6 },
  { title: '7', value: 7 },
  { title: '8', value: 8 },
  { title: '9', value: 9 },
  { title: '10 (максимальный)', value: 10 },
];

// Computed
const hasErrors = computed(() => {
  return queries.value.some(q => q.error && q.error.length > 0);
});

// Watch for prop changes (only update if different to avoid loops)
watch(
  () => props.modelValue,
  (newValue) => {
    if (JSON.stringify(newValue) === JSON.stringify(queries.value)) return;

    if (!newValue || newValue.length === 0) {
      queries.value = [];
    } else {
      queries.value = newValue.map(q => ({
        query_text: q.query_text || '',
        search_in: q.search_in !== undefined ? q.search_in : null,  // Keep null if explicitly set
        is_excluded: q.is_excluded || false,
        operator: q.operator || 'AND',
        weight: q.weight || 5,
        order_num: q.order_num || 0,
        error: '',
      }));
    }
  },
  { immediate: true, deep: true }
);

// Watch for queries changes (only emit if different to avoid loops)
let isUpdating = false;
watch(
  queries,
  (newValue) => {
    if (isUpdating) return;

    // Update order_num before emitting
    const updatedQueries = newValue.map((q, index) => ({
      ...q,
      order_num: index,
    }));

    if (JSON.stringify(updatedQueries) !== JSON.stringify(props.modelValue)) {
      isUpdating = true;
      emit('update:modelValue', updatedQueries);
      setTimeout(() => { isUpdating = false; }, 0);
    }
  },
  { deep: true }
);

// Methods
const addQuery = () => {
  queries.value.push({
    query_text: '',
    search_in: null,  // null means use project default
    is_excluded: false,
    operator: 'AND',
    weight: 5,
    order_num: queries.value.length,
    error: '',
  });
};

const removeQuery = (index) => {
  queries.value.splice(index, 1);
};

const moveUp = (index) => {
  if (index > 0) {
    const temp = queries.value[index];
    queries.value[index] = queries.value[index - 1];
    queries.value[index - 1] = temp;
  }
};

const moveDown = (index) => {
  if (index < queries.value.length - 1) {
    const temp = queries.value[index];
    queries.value[index] = queries.value[index + 1];
    queries.value[index + 1] = temp;
  }
};

const insertOperator = (index, operator) => {
  const vTextarea = textareaRefs.value[index];
  const textarea = vTextarea?.$el?.querySelector('textarea');
  if (!textarea) return;

  const text = queries.value[index].query_text || '';

  // Use saved cursor position (from blur) or fall back to end of text
  const savedPos = cursorPositions.value[index];
  const start = savedPos?.start ?? text.length;
  const end = savedPos?.end ?? text.length;

  // Special handling for quotes and parentheses - wrap selected text
  if (operator === '""') {
    const selectedText = text.substring(start, end);
    queries.value[index].query_text =
      text.substring(0, start) + `"${selectedText}"` + text.substring(end);
  } else if (operator === '()') {
    const selectedText = text.substring(start, end);
    queries.value[index].query_text =
      text.substring(0, start) + `(${selectedText})` + text.substring(end);
  } else {
    // Insert operator at cursor position
    queries.value[index].query_text =
      text.substring(0, start) + operator + text.substring(end);
  }

  // Restore focus and set cursor position after the inserted operator
  const newCursorPos = start + operator.length;
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    // Update saved position
    cursorPositions.value[index] = { start: newCursorPos, end: newCursorPos };
  }, 0);

  validateQuery(index);
};

const validateQuery = async (index) => {
  const query = queries.value[index];

  if (!query.query_text || query.query_text.trim() === '') {
    query.error = 'Запрос не может быть пустым';
    return;
  }

  // Basic validation
  const text = query.query_text.trim();

  // Check for unmatched quotes
  const quoteCount = (text.match(/"/g) || []).length;
  if (quoteCount % 2 !== 0) {
    query.error = 'Некорректное количество кавычек';
    return;
  }

  // Check for unmatched parentheses
  const openParen = (text.match(/\(/g) || []).length;
  const closeParen = (text.match(/\)/g) || []).length;
  if (openParen !== closeParen) {
    query.error = 'Некорректное количество скобок';
    return;
  }

  // Clear error if basic validation passes
  query.error = '';

  // Optional: call API for advanced validation
  // TODO: Uncomment when /search/validate endpoint is implemented
  // try {
  //   await apiClient.post('/search/validate', {
  //     query: text,
  //   });
  //   query.error = '';
  // } catch (error) {
  //   if (error.response && error.response.data && error.response.data.detail) {
  //     query.error = error.response.data.detail;
  //   }
  // }
};

// --- QLang generator (свободный текст → LLM → QLang) ---
const genDialog = ref(false);
const genText = ref('');
const genLoading = ref(false);
const genError = ref('');
const genResult = ref(null);       // { query, explanation, valid, error }
const genCount = ref(null);        // число совпадений за 30 дней (null = не считали)
const genCountLoading = ref(false);

const openGenerator = () => {
  genText.value = '';
  genResult.value = null;
  genError.value = '';
  genCount.value = null;
  genDialog.value = true;
};

const runGenerate = async () => {
  const text = genText.value.trim();
  if (!text) return;
  genLoading.value = true;
  genError.value = '';
  genResult.value = null;
  genCount.value = null;
  try {
    const { data } = await apiClient.post('/qlang/generate', { text });
    genResult.value = {
      query: data.query || '',
      explanation: data.explanation || '',
      valid: !!data.valid,
      error: data.error || '',
    };
    if (genResult.value.query) runCount();
  } catch (e) {
    genError.value = e?.response?.data?.detail || 'Не удалось сгенерировать запрос';
  } finally {
    genLoading.value = false;
  }
};

// Пересчёт совпадений + перепроверка валидности (count-эндпоинт возвращает и valid).
const runCount = async () => {
  const query = genResult.value?.query?.trim();
  if (!query) { genCount.value = null; return; }
  genCountLoading.value = true;
  try {
    const { data } = await apiDataClient.post('/search/count', { query, days: 30 });
    if (genResult.value) {
      genResult.value.valid = !!data?.valid;
      if (!data?.valid && data?.message) genResult.value.error = data.message;
    }
    genCount.value = data?.valid ? data.count : null;
  } catch (e) {
    genCount.value = null;
  } finally {
    genCountLoading.value = false;
  }
};

const insertGenerated = () => {
  const query = genResult.value?.query?.trim();
  if (!query) return;
  // Заполняем первый пустой запрос либо добавляем новый.
  let idx = queries.value.findIndex(q => !q.query_text || !q.query_text.trim());
  if (idx === -1) {
    queries.value.push({
      query_text: query,
      search_in: null,
      is_excluded: false,
      operator: 'AND',
      weight: 5,
      order_num: queries.value.length,
      error: '',
    });
    idx = queries.value.length - 1;
  } else {
    queries.value[idx].query_text = query;
  }
  genDialog.value = false;
  validateQuery(idx);
};

// Initialize with one empty query if none provided
if (props.modelValue.length === 0) {
  addQuery();
}
</script>

<style scoped>
.drag-handle {
  cursor: move;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-4 {
  gap: 1rem;
}
</style>
